import React, { useRef, useEffect, useState } from "react";
import { settingsService } from "@/services/SettingsService";
import { chatService } from "@/services/ChatService";
import ChatMessage from "../ChatMessage";
import ChatInput from "../ChatInput";
import { MessageSquare, Sparkles, Maximize2, Minimize2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useAppConfig } from "@/hooks/useAppConfig";
import "./ChatInterface.css";

interface ChatInterfaceProps {
  zenMode?: boolean;
  onToggleZenMode?: () => void;
  pageRoute?: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  zenMode = false,
  onToggleZenMode,
  pageRoute = 'home'
}) => {
  const { messages, isLoading, addMessage, setLoading, vehicleData } = useApp();
  const { getPageConfig } = useAppConfig();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const settings = settingsService.getSettings();

  // Get page-specific configuration
  const pageConfig = getPageConfig(pageRoute);

  const scrollToBottom = () => {
    if (settings.chat.autoScroll && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSuggestionClick = async (suggestion: string) => {
    addMessage({
      type: "user",
      content: suggestion,
    });

    setLoading(true);

    try {
      const response = await chatService.searchParts(vehicleData, suggestion);

      if (response.success) {
        if (response.responseType === 'markdown') {
          const responseMessage = chatService.generateChatResponse(
            [],
            [],
            suggestion,
            undefined,
            response.markdownContent,
            'markdown'
          );

          addMessage({
            type: "assistant",
            content: responseMessage.content,
            responseType: responseMessage.responseType,
            markdownContent: responseMessage.markdownContent,
          });
        } else if (response.parts || response.vmrsParts) {
        const responseMessage = chatService.generateChatResponse(
            response.parts || [],
          response.vmrsParts || [],
            suggestion,
            response.pt,
            undefined,
            'json'
        );

        addMessage({
          type: "assistant",
          content: responseMessage.content,
          pcdbParts: responseMessage.pcdbParts,
          vmrsParts: responseMessage.vmrsParts,
            pt: response.pt,
            responseType: responseMessage.responseType,
        });
        }
      } else {
        addMessage({
          type: "assistant",
          content:
            response.error ||
            "Sorry, I couldn't find any parts for your search. Please try again with different terms.",
        });
      }
    } catch (error) {
      console.error("Search error:", error);
      addMessage({
        type: "assistant",
        content:
          "Sorry, there was an error processing your request. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modern-chat-interface">
      <div className="chat-header-modern">
        <div className="header-content-modern">
          <div className="header-icon-modern">
            <MessageSquare size={24} />
          </div>
          <div className="header-text-modern">
            <h1 className="header-title-modern">{pageConfig?.name || 'Parts Counter Assistant'}</h1>
            <p className="header-subtitle-modern">
              {pageConfig?.subTitle || 'Just tell me what you need - I\'ll help you find it'}
            </p>
          </div>
          <div className="header-actions-modern">
            {onToggleZenMode && (
              <button
                className="zen-mode-toggle"
                onClick={onToggleZenMode}
                title={zenMode ? "Exit Zen Mode" : "Enter Zen Mode"}
                aria-label={zenMode ? "Exit Zen Mode" : "Enter Zen Mode"}
              >
                {zenMode ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="chat-messages-modern">
        {messages.length === 0 ? (
          <div className="empty-state-modern">
            <div className="welcome-card-modern">
              <div className="welcome-icon-modern">
                <MessageSquare size={40} />
              </div>
              <h2 className="welcome-title">{pageConfig?.welcomeTitle || 'Welcome to Parts Counter'}</h2>
              <p className="welcome-description">
                {pageConfig?.welcomeSubTitle || 'What parts are you looking for? Type below or select from the options shown'}
              </p>
              <div className="suggestions-modern">
                {pageConfig?.trendingSearches && pageConfig.trendingSearches.length > 0 && (
                  <>
                <h3 className="suggestions-title">Trending searches:</h3>
                <div className="suggestion-pills">
                      {pageConfig.trendingSearches.map((suggestion) => (
                    <button
                      key={suggestion}
                      className="suggestion-pill"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="messages-container-modern">
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                showTimestamp={settings.chat.showTimestamps}
                compact={settings.chat.compactMode}
                pageRoute={pageRoute}
              />
            ))}
          </div>
        )}

        {isLoading && (
          <div className="loading-indicator-modern">
            <div className="loading-content-modern">
              <div className="loading-spinner">
                <div className="spinner-ring"></div>
              </div>
              <span className="loading-text">Searching...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <ChatInput pageRoute={pageRoute} />
    </div>
  );
};

export default ChatInterface;
